import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function UpdationHistory() {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedOperation, setSelectedOperation] = useState("");
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const role = localStorage.getItem("role");
  const adminToken = localStorage.getItem("adminToken");
  const userToken = localStorage.getItem("userToken");
  const token = role === "admin" ? adminToken : userToken;

  /* FETCH HISTORY */
  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/history/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistoryList(res.data || []);
    } catch (error) {
      console.error("Fetch History Error:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  /* OPERATIONS */
  const operations = [...new Set(historyList.map((v) => v.operationType))];

  /* FILTERING */
  const filtered = historyList.filter((v) => {
    const opMatch = selectedOperation ? v.operationType === selectedOperation : true;

    const text = searchText.trim().toLowerCase();
    const searchMatch = text
      ? [
          v.operatorId,
          v.operatedBy,
          v.entityName,
          v.fieldName,
          v.oldValue,
          v.newValue,
          v.operationType,
        ].some((field) => String(field || "").toLowerCase().includes(text))
      : true;

    return opMatch && searchMatch;
  });

  /* PAGINATION */
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const currentData = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  /* OPERATION BADGE COLOR */
  const getOperationClass = (type) => {
    const lower = type?.toLowerCase();
    if (lower?.includes("create")) return "create";
    if (lower?.includes("update")) return "update";
    if (lower?.includes("delete")) return "delete";
    return "other";
  };

  return (
    <>
      <div className="history-wrapper">
        {/* Header */}
        <header className="page-header">
          <h1 className="page-title">Updation History</h1>
          <div className="total-card">
            <span className="total-count">{filtered.length}</span>
            <span className="total-label">Total Records</span>
          </div>
        </header>

        {/* Filters */}
        <section className="filters-section">
          <div className="filter-group">
            <label>Operation Type</label>
            <select
              value={selectedOperation}
              onChange={(e) => {
                setSelectedOperation(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Operations</option>
              {operations.map((op, i) => (
                <option key={i} value={op}>{op}</option>
              ))}
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Entity, field, value, operator..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading history records...</p>
            </div>
          ) : currentData.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-history"></i>
              <h3>No History Records Found</h3>
              <p>No update operations recorded yet.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Entity</th>
                      <th>Field</th>
                      <th>Operation</th>
                      <th>Old Value</th>
                      <th>New Value</th>
                      <th>Operated By</th>
                      <th>Operator ID</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => {
                      const opClass = getOperationClass(item.operationType);

                      return (
                        <tr key={item.id} className="table-row">
                          <td>{index + 1 + (currentPage - 1) * rowsPerPage}</td>
                          <td className="entity-cell">{item.entityName}</td>
                          <td>{item.fieldName}</td>
                          <td>
                            <span className={`operation-badge ${opClass}`}>
                              {item.operationType}
                            </span>
                          </td>
                          <td>{item.oldValue ?? "—"}</td>
                          <td>{item.newValue ?? "—"}</td>
                          <td>{item.operatedBy}</td>
                          <td>{item.operatorId}</td>
                          <td>
                            {item.time
                              ? new Date(item.time.split(".")[0]).toLocaleString("en-IN")
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {currentData.map((item, index) => {
                  const opClass = getOperationClass(item.operationType);

                  return (
                    <div key={item.id} className="history-card">
                      <div className="card-header">
                        <span className="index">#{index + 1 + (currentPage - 1) * rowsPerPage}</span>
                        <span className={`operation-badge ${opClass}`}>
                          {item.operationType}
                        </span>
                      </div>
                      <div className="card-details">
                        <div><strong>Entity:</strong> {item.entityName}</div>
                        <div><strong>Field:</strong> {item.fieldName}</div>
                        <div><strong>Old → New:</strong> {item.oldValue ?? "—"} → {item.newValue ?? "—"}</div>
                        <div><strong>By:</strong> {item.operatedBy} (ID: {item.operatorId})</div>
                        <div><strong>Time:</strong> {item.time ? new Date(item.time.split(".")[0]).toLocaleString("en-IN") : "—"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToPage(i + 1)}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>

              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rows-select"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={30}>30 / page</option>
              </select>
            </div>
          )}
        </section>
      </div>

      <style jsx>{`
        .history-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .filters-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .filter-group {
          flex: 1;
          min-width: 250px;
          display: flex;
          flex-direction: column;
        }

        .filter-group label {
          font-weight: 600;
          margin-bottom: 8px;
          color: white;
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input,
        .filter-group select {
          width: 100%;
          padding: 16px 20px;
          padding-left: 52px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 16px;
          color: white;
          font-size: 1rem;
        }

        .filter-group select {
          padding-left: 20px;
        }

        .search-input-wrapper input:focus,
        .filter-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          transition: all 0.3s ease;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .table-row td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .entity-cell {
          font-weight: 600;
          color: white;
        }

        .operation-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .operation-badge.create {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .operation-badge.update {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
        }

        .operation-badge.delete {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .operation-badge.other {
          background: rgba(255, 193, 7, 0.2);
          color: #f59e0b;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .history-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 44px;
          height: 44px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #3b82f6;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          font-weight: 600;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .rows-select {
          padding: 12px 16px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}





// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function UpdationHistory() {
//   const [historyList, setHistoryList] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [selectedOperation, setSelectedOperation] = useState("");
//   const [searchText, setSearchText] = useState("");

//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(30);

//   //  TOKEN LOGIC (UNCHANGED)
//   const role = localStorage.getItem("role");
//   const adminToken = localStorage.getItem("adminToken");
//   const userToken = localStorage.getItem("userToken");
//   const token = role === "admin" ? adminToken : userToken;



//   //  FETCH HISTORY
//   const fetchHistory = async () => {
//     try {
//       setLoading(true);

//       const res = await axios.get(`${BASE_URL}/api/history/all`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       setHistoryList(res.data || []);
//     } catch (error) {
//       console.error("Fetch History Error:", error);
//       toast.error("Failed to load history");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchHistory();
//   }, []);

//   /* ---------------- FILTER + SEARCH (BASED ON REAL KEYS) ---------------- */
//   const operations = [...new Set(historyList.map((v) => v.operationType))];

//   const filtered = historyList.filter((v) => {
//     const opMatch = selectedOperation
//       ? v.operationType === selectedOperation
//       : true;

//     const text = searchText.trim().toLowerCase();
//     const searchMatch = text
//       ? [
//         v.operatorId,
//           v.operatedBy,
//           v.entityName,
//           v.fieldName,
//           v.oldValue,
//           v.newValue,
//           v.operationType,
//         ].some((field) =>
//           String(field || "").toLowerCase().includes(text)
//         )
//       : true;

//     return opMatch && searchMatch;
//   });

//   /* ---------------- PAGINATION ---------------- */
//   const totalPages = Math.ceil(filtered.length / rowsPerPage);

//   const currentData = filtered.slice(
//     (currentPage - 1) * rowsPerPage,
//     currentPage * rowsPerPage
//   );

//   const goToPage = (page) => {
//     if (page >= 1 && page <= totalPages) setCurrentPage(page);
//   };

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>Updation History</h2>

//       {/* ------------ FILTERS ------------ */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Operation</label>
//           <select
//             value={selectedOperation}
//             onChange={(e) => {
//               setSelectedOperation(e.target.value);
//               setCurrentPage(1);
//             }}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             {operations.map((d, i) => (
//               <option key={i}>{d}</option>
//             ))}
//           </select>
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={searchText}
//             onChange={(e) => {
//               setSearchText(e.target.value);
//               setCurrentPage(1);
//             }}
//             placeholder="Search by entity, field, value..."
//             style={styles.input}
//           />
//         </div>
//       </div>

//       {/* ------------ TABLE ------------ */}
//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : currentData.length === 0 ? (
//           <div style={styles.noData}>No History Records Found</div>
//         ) : (
//           <div style={styles.tableWrapper}>
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.th}>#</th>
//                   <th style={styles.th}>Entities</th>
//                   <th style={styles.th}>Field Name</th>
//                   <th style={styles.th}>Status</th>
//                   <th style={styles.th}>Old Value</th>
//                   <th style={styles.th}>New Value</th>
//                   <th style={styles.th}>Operated By</th>
//                   <th style={styles.th}>Operator ID</th>
//                   <th style={styles.th}>Time</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {currentData.map((item, index) => {
//                   return (
//                     <tr key={item.id} style={styles.row}>
//                       <td style={styles.td}>
//                         {index + 1 + (currentPage - 1) * rowsPerPage}
//                       </td>
//                       <td style={styles.td}>{item.entityName}</td>
//                       <td style={styles.td}>{item.fieldName}</td>
                      
//                       <td style={styles.td}>
//                         <span style={styles.badge}>
//                           {item.operationType}
//                         </span>
//                       </td>
//                       <td style={styles.td}>{item.oldValue ?? "N/A"}</td>
//                       <td style={styles.td}>{item.newValue ?? "N/A"}</td>
//                       <td style={styles.td}>{item.operatedBy}</td>
//                       <td style={styles.td}>{item.operatorId}</td>
//                       <td style={styles.td}>
//                         {item.time
//                           ? new Date(item.time.split(".")[0]).toLocaleString("en-IN")
//                           : "N/A"}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>

//             {/* ------------ PAGINATION ------------ */}
//             {totalPages > 1 && (
//               <div style={styles.paginationContainer}>
//                 <button
//                   disabled={currentPage === 1}
//                   style={styles.pageBtn}
//                   onClick={() => goToPage(currentPage - 1)}
//                 >
//                   Prev
//                 </button>

//                 {[...Array(totalPages)].map((_, i) => (
//                   <button
//                     key={i}
//                     style={{
//                       ...styles.pageBtn,
//                       ...(currentPage === i + 1 ? styles.activePage : {}),
//                     }}
//                     onClick={() => goToPage(i + 1)}
//                   >
//                     {i + 1}
//                   </button>
//                 ))}

//                 <button
//                   disabled={currentPage === totalPages}
//                   style={styles.pageBtn}
//                   onClick={() => goToPage(currentPage + 1)}
//                 >
//                   Next
//                 </button>

//                 <select
//                   value={rowsPerPage}
//                   style={styles.rowsSelect}
//                   onChange={(e) => {
//                     setRowsPerPage(Number(e.target.value));
//                     setCurrentPage(1);
//                   }}
//                 >
//                   <option value={30}>30 / Page</option>
//                   <option value={20}>20 / Page</option>
//                   <option value={10}>10 / Page</option>
//                 </select>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// /*  SAME STYLE STRUCTURE */
// const styles = {
//   container: { padding: 30, background:"linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))", },
//   title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },

//   filterRow: { display: "flex", gap: 20, marginBottom: 25, flexWrap: "wrap" },
//   filterCard: {
//      flex: 3, padding: 16, borderRadius: 12, background: "rgba(255,255,255,0.45)", 
//     backdropFilter: "blur(8px)",boxShadow: "0 4px 15px rgba(0,0,0,0.01)" },
//   filterLabel: { fontSize: 14, fontWeight: 600 },

//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   input: {
//     width: "100%",
//     padding: 12,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   card: {
//    background: "rgba(255,255,255,0.7)",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//   },

//   tableWrapper: { overflowX: "auto" },
//   table: { width: "100%", minWidth: 1100, borderCollapse: "collapse" },

//   th: {
//    background: "#b8b8f3ff",
//     padding: "14px 10px",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//   },

//   row: { height: 62 },
//   td: { padding: "12px 10px", borderBottom: "1px solid #eee" },

//   badge: {
//     background: "#d7ecff",
//     padding: "6px 12px",
//     borderRadius: 8,
//     color: "#005bb7",
//     fontWeight: 600,
//   },

//   noData: { textAlign: "center", padding: 40, color: "#777" },

//   paginationContainer: {
//     display: "flex",
//     gap: 10,
//     justifyContent: "center",
//     marginTop: 15,
//   },
//   pageBtn: {
//     padding: "6px 14px",
//     borderRadius: 6,
//     border: "1px solid #1967d2",
//     background: "white",
//   },
//   activePage: { background: "#1967d2", color: "#fff" },
//   rowsSelect: { padding: "6px 14px", borderRadius: 6 },
// };
